/**
 * Topic clusters for the aircon guides (the WordPress "hvac-services" posts
 * served at /sg/aircon-servicing-singapore/{slug}, plus the in-repo guides).
 *
 * One definition feeds both directions of internal linking:
 *  - the hub links each cluster's pillar guide (AirconGuides);
 *  - every guide links its cluster siblings and the service page that the
 *    cluster is about ([slug].js).
 * Guides used to link only the six newest posts in the category, whatever they
 * were about, and the hub did not link any guide at all.
 *
 * A post is assigned by keywords in its slug, first match wins - so order
 * matters ("chemical" must beat the generic servicing words). Slugs that match
 * nothing fall into the servicing cluster.
 *
 * Pillar slugs must exist: /sg/aircon-servicing-singapore/[slug] is
 * `fallback: false`, so a pillar whose post leaves the category becomes a 404
 * link. [slug].js only lists related posts it has actually fetched.
 */
const HUB = '/sg/aircon-servicing-singapore'

export const AIRCON_GUIDE_CLUSTERS = [
    {
        key: 'chemical',
        title: 'Chemical wash & overhaul',
        blurb: 'When a normal service is not enough, and which deep clean you need.',
        service: { href: `${HUB}/aircon-chemical-wash-and-overhaul`, label: 'Book a chemical wash or overhaul' },
        pillar: { slug: 'chemical-wash-vs-chemical-overhaul-which-is-the-best', title: 'Chemical wash vs chemical overhaul' },
        keywords: ['chemical', 'overhaul'],
    },
    {
        key: 'gas',
        title: 'Gas top-up & refrigerant',
        blurb: 'Signs of low refrigerant, pressure tests and what a top-up costs.',
        service: { href: `${HUB}/aircon-gas-top-up`, label: 'Book an aircon gas top-up' },
        pillar: { slug: '3-ways-to-know-if-your-aircon-needs-a-gas-refill', title: '3 ways to know your aircon needs a gas refill' },
        keywords: ['gas', 'refrigerant', 'pressure-test'],
    },
    {
        key: 'leaks',
        title: 'Leaks & condensation',
        blurb: 'Dripping units, wet trunking and choked drainage - causes and fixes.',
        service: { href: `${HUB}/aircon-leaking-water`, label: 'Fix a leaking aircon' },
        pillar: { slug: 'aircon-leaking-water', title: 'Aircon leaking water: causes, fixes and cost' },
        keywords: ['leak', 'condensation', 'insulation', 'trunking', 'drain', 'water'],
    },
    {
        key: 'repair',
        title: 'Not cooling, noise & repairs',
        blurb: 'Diagnosing warm air, strange noises, smells, error lights and failed parts.',
        service: { href: `${HUB}/aircon-repair`, label: 'Book an aircon repair' },
        pillar: { slug: 'aircon-not-cooling', title: 'Why your aircon is not blowing cold air' },
        keywords: ['not-cooling', 'nois', 'smell', 'blinking', 'overheat', 'stops-working', 'problem', 'compressor',
            'condenser', 'evaporator', 'pcb', 'printed-circuit', 'thermistor', 'thermostat', 'capacitor', 'fan-motor',
            'blower', 'warranty', 'replace', 'poor-air-quality'],
    },
    {
        key: 'buying',
        title: 'Choosing & installing an aircon',
        blurb: 'Brands, inverter vs non-inverter, system types and installation.',
        service: { href: `${HUB}/aircon-installation`, label: 'Book an aircon installation' },
        pillar: { slug: 'compare-top-air-conditioner-brands-in-singapore', title: 'Comparing aircon brands in Singapore' },
        keywords: ['inverter', 'split', 'brand', 'prism', 'lg', 'portable', 'air-cooler', 'dehumidifier', 'installation',
            'living-room', 'tengah', 'centralised', 'shopping-malls'],
    },
    {
        key: 'energy',
        title: 'Energy use & comfort',
        blurb: 'Running costs, the right temperature and healthy use at home.',
        service: { href: `${HUB}/cleaning-maintenance-service`, label: 'Book general aircon servicing' },
        pillar: { slug: 'best-ways-to-optimize-your-aircon-for-energy-efficiency', title: 'Optimising your aircon for energy efficiency' },
        keywords: ['energy', 'electricity', 'temperature', 'sleep', 'running', 'hours', 'mode', 'ceiling-fan',
            'dry-clothes', 'sore-throat', 'asthma', 'baby', 'mosquito', 'dengue', 'rainy', 'holiday', 'festive'],
    },
    {
        key: 'servicing',
        title: 'Servicing & maintenance',
        blurb: 'How often to service, what a service covers and choosing a provider.',
        service: { href: `${HUB}/cleaning-maintenance-service`, label: 'Book general aircon servicing' },
        pillar: { slug: 'how-often-should-i-service-aircon-for-best-performance', title: 'How often to service your aircon' },
        keywords: [],
    },
]

const FALLBACK = AIRCON_GUIDE_CLUSTERS[AIRCON_GUIDE_CLUSTERS.length - 1]

export const guideHref = (slug) => `${HUB}/${slug}`

/** The cluster a guide belongs to, by its slug. Never null. */
export const clusterForSlug = (slug) => {
    const s = String(slug ?? '').toLowerCase()
    return AIRCON_GUIDE_CLUSTERS.find((c) => c.keywords.some((k) => s.includes(k))) ?? FALLBACK
}

/**
 * Up to `count` other guides in the same cluster as `slug`, pillar first.
 * `posts` is [{ slug, title }] of guides known to exist.
 */
export const relatedGuides = (slug, posts, count = 4) => {
    const cluster = clusterForSlug(slug)
    const seen = new Set([slug])
    const sameCluster = (posts ?? []).filter((p) => p?.slug && clusterForSlug(p.slug).key === cluster.key)
    const pillar = sameCluster.find((p) => p.slug === cluster.pillar.slug)
    return [pillar, ...sameCluster]
        .filter((p) => p && !seen.has(p.slug) && seen.add(p.slug))
        .slice(0, count)
}
